import { useCallback } from "react";
import { useI18nContext } from "./useI18nContext";

/**
 * Returns formatList function that takes a list of strings, and returns
 * a concatenated version that's appropriate for the current locale.
 *
 * At the time of this comment, TypeScript doesn't have the definitions of
 * Intl.ListFormat. But Intl.ListFormat is widely available at this point
 * so it's just a matter of avoiding compiler errors that I had to write some
 * ts-ignore in the code below.
 */
export function useI18nList() {
  const { language: locale } = useI18nContext();
  const formatList = useCallback(
    (items: string[], options?: Intl_ListFormatOptions) => {
      // Intl.ListFormat defaults to type: "conjunction" but our default is different
      const optionsWithDefault: Intl_ListFormatOptions = {
        type: "unit",
        ...options,
      };
      const listFormat = intlListFormatFor(locale, optionsWithDefault);
      return listFormat.format(items);
    },
    [locale]
  );

  return { formatList };
}

// Cache Intl.ListFormat because Intl objects are fairly expensive to create
const intlListFormatCache: Record<string, Intl_ListFormat> = {};

function intlListFormatFor(locale: string, options?: Intl_ListFormatOptions) {
  const key = `${locale}:::${JSON.stringify(options ?? {})}`;
  let intlListFormat = intlListFormatCache[key];
  if (intlListFormat == null) {
    intlListFormat = new Intl.ListFormat(locale, options);
    intlListFormatCache[key] = intlListFormat;
  }
  return intlListFormat;
}

// Once we update type definitions we will not need these
export interface Intl_ListFormatOptions {
  type?: "conjunction" | "disjunction" | "unit";
  style?: "long" | "short" | "narrow";
}

type Intl_ListFormat = {
  format: (values: string[]) => string;
};
